const { kebabCase } = require('lodash');
const {
    HTML_CLASS_ATTRIBUTE,
    HTML_STYLE_ATTRIBUTE,
    ATTRIBUTES_DOM_ALIAS_MAP
} = require('./constants');

/**
 * @typedef  {Object} ElementJson
 * @property {String} tag The element's tag name.
 * @property {ElementJson[]} [children] The element's children elements.
 */

/**
 * Converts a style object to an HTML string representation.
 * @param {Object} style - Style object representation e.g { color: 'blue' }
 * @returns {String}
 * @example
 *
 * const styleString = styleObjectToString({ color: 'blue', border: '1px solid black'}) // = 'color:blue;border:1px solid black'
 */
const styleObjectToString = (style = {}) => {
    let styleString = '';
    for (const styleKey in style) {
        styleString += `${kebabCase(styleKey)}: ${style[styleKey]}; `;
    }

    return styleString.trim();
};

/**
 * Converts a map of attributes to an HTML string representation.
 * @param  {Object} attributes A map of element attributes, where the key is the attribute name, and the value is its value.
 * @return {String}
 * @example
 *
 * const attributes = {
 *   a: 1,
 *   b: 2
 * };
 *
 * const attributesString = attributesToHTMLString(attributes); // 'a="1" b="2"'
 */
const attributesToHTMLString = (attributes) => {
    let string = '';

    for (const attribute in attributes) {
        const value = attributes[attribute];

        switch (attribute) {
            case HTML_STYLE_ATTRIBUTE:
                string += `${attribute}="${styleObjectToString(value)}" `;
                break;
            case 'className':
                string += `${HTML_CLASS_ATTRIBUTE}="${value}" `;
                break;
            default:
                const attributeName = ATTRIBUTES_DOM_ALIAS_MAP[attribute] || attribute;
                string += `${attributeName}="${value}" `;
        }
    }

    return string.trim();
};

/**
 * Converts a JSON representation of an element to an HTML string.
 * Will iterate the JSON recursively, traversing deeper in "children" keys.
 * @param  {ElementJson} json The json to convert.
 * @return {String}
 */
const jsonToHTMLString = (json) => {
    const { tag, children = [], childElements = [], ...rest } = json;
    const _children = children.length ? children : childElements;

    const attributesString = attributesToHTMLString(rest);
    const childStrings = Array.isArray(_children) ? _children.map(jsonToHTMLString).join('') : _children;

    return `<${tag} ${attributesString}>${childStrings}</${tag}>`;
};

module.exports = jsonToHTMLString;
